/**
 * M2: the marketplace facet of the V2 catalog (`GET catalog/conflict/false/markets`) and the `markets` list filter.
 * TODO(M2): switch these types to @skupreme/api-contracts once the server contract (kiro/m2-region-filter-server) merges.
 */
import {
  buildCatalogV2ListParams,
  type CatalogListFilter,
  type CatalogV2Filter,
} from './list-params';
import {
  countryName,
  countryOfMarketplace,
  MARKET_REGIONS,
  NO_COUNTRY,
} from './markets';

export type CatalogMarketsResponse = {
  source: 'v2';
  /**
   * Per listing marketplace id in scope ('' for stores with no country included), and again per connection:
   * `connection_id` is null on the per marketplace row and set on the per connection ones.
   */
  markets: {
    marketplace_id: string;
    connection_id: string | null;
    items: number;
    listings: number;
  }[];
  /** At the account level: the marketplace ids each division lists in. */
  divisions?: { division_id: string; marketplace_ids: string[] }[];
};

/** The list and pagination query's `markets`: comma-separated marketplace ids, `none` for no-country stores. */
export type CatalogMarketsQuery = { markets?: string };

/** A country (or NO_COUNTRY) of the facet with its distinct items (summed over its marketplace ids from an older API). */
export type MarketCount = { country: string; items: number };

/* What the facet does not take from the list's query: the countries it counts, and the page. */
const NOT_FACET_PARAMS = ['markets', 'page', 'limit'];

/** The list's query (what `use-catalog-list` asks with) minus the countries and the page, for the facet. */
export function facetParams(
  filter: CatalogListFilter,
  v2Filter: CatalogV2Filter,
  search: string,
) {
  const query = buildCatalogV2ListParams({ ...filter, search }, v2Filter);
  return Object.fromEntries(
    Object.entries(query).filter(([key]) => !NOT_FACET_PARAMS.includes(key)),
  );
}

const countryOrNone = (marketplaceId: string) =>
  countryOfMarketplace(marketplaceId) ?? NO_COUNTRY;

/** Picker order: MARKET_REGIONS, then the no-country stores last. */
const ORDER = [...MARKET_REGIONS.flatMap(r => r.countries), NO_COUNTRY];

export const byPickerOrder = (a: string, b: string) =>
  ORDER.indexOf(a) - ORDER.indexOf(b);

/**
 * The facet folded into countries, in picker order, as picking a country returns items. EN-4574: the server's
 * `countries` (distinct items over each country's marketplace ids, '' for no country) when it sends them; an older API
 * sends only the rows per marketplace id ('' for stores with no country included), summed per country here from the per
 * marketplace rows only (an item on two connections of one marketplace counts once), so an item listed in two
 * marketplaces of one country counts twice.
 */
export function foldMarkets({
  markets,
  countries,
}: Pick<CatalogMarketsResponse, 'markets'> & {
  countries?: MarketCount[];
}): MarketCount[] {
  const items = new Map<string, number>();
  if (countries) {
    for (const { country, items: n } of countries)
      items.set(country || NO_COUNTRY, n);
  } else {
    for (const { marketplace_id, connection_id, items: n } of markets) {
      if (connection_id) continue;
      const country = countryOrNone(marketplace_id);
      items.set(country, (items.get(country) ?? 0) + n);
    }
  }
  return [...items.keys()]
    .sort(byPickerOrder)
    .map(country => ({ country, items: items.get(country) ?? 0 }));
}

/** Division id -> its one country, for the divisions whose marketplace ids all sell in the same country. */
export function divisionCountries(
  divisions: CatalogMarketsResponse['divisions'],
): ReadonlyMap<string, string> {
  const single = new Map<string, string>();
  for (const { division_id, marketplace_ids } of divisions ?? []) {
    const countries = new Set(marketplace_ids.map(countryOfMarketplace));
    const [only] = countries;
    if (countries.size === 1 && only) single.set(division_id, only);
  }
  return single;
}

/** "Mexico"; the no-country token reads "Online stores (no country)". */
export const marketName = (country: string) =>
  country === NO_COUNTRY ? 'Online stores (no country)' : countryName(country);
